/**
 * shared/types/wire.ts — WebSocket envelope: client/server frames, message rows, frame parsing, presence/typing flags and history/search limits.
 *
 * Moved verbatim out of the former shared/types.ts monolith; the public
 * surface is unchanged and shared/types/index.ts re-exports it.
 */
import { MAX_THREAD_TEXT } from '../security.ts';
import type { Role } from './couple.ts';

/* ------------------------------------------------------------------ *
 * 4. WebSocket message envelope
 * ------------------------------------------------------------------ */

export type ErrorCode =
  | 'illegal_transition'
  | 'not_revealed'
  | 'invalid_code'
  | 'expired_code'
  | 'code_consumed'
  | 'code_collision'
  | 'couple_full'
  /* Card Vf23eSKKxDhX — the one-way door has a door now. `couple_closed` is
     the tombstone: the room's last member left, so its old invite code is
     refused rather than silently re-admitted. `not_in_couple` names the
     account that asked to leave something it does not belong to. */
  | 'couple_closed'
  | 'not_in_couple'
  /* card j1pvawhlLBks: refreshInvite() refuses once the couple is complete */
  | 'already_paired'
  /* Card tJdisOEkVwha — the emailed 24h link. Three distinct answers, because
     "it did not work" is the one thing the partner cannot act on: the link is
     unknown/malformed, already used (single-use), or past its 24 hours. */
  | 'link_invalid'
  | 'link_used'
  | 'link_expired'
  | 'bad_token'
  | 'bad_payload'
  /* Google Photos picker (epic lARMIX56PSIY): each failure names what the
     person can do — reconnect, wait, or tell us the API is off. */
  | 'not_configured'
  | 'rate_limited'
  | 'photos_not_connected'
  | 'photos_signin_required'
  | 'photos_forbidden'
  | 'photos_api_disabled'
  | 'photos_failed'
  /* Card aWObthOOOhyI — the cap answers in words instead of silently
     slicing: too_many_photos (over ten in one pick, or the memory is full)
     and no_photos (the pick contained only video). */
  | 'too_many_photos'
  | 'no_photos'
  | 'not_found'
  /* Invite-only (Jonathan, 2026-10-23): PEAR_SIGNUP_MODE is not 'open', so no
     new account may be created — on /api/auth/register or through the Google
     callback. Sign-in for existing accounts is unaffected. */
  | 'signups_closed'
  /* Card Q4BluQX8fXgj — only the creator may remove an entry, shared or not:
     a partner's delete is refused with the reason named, not swallowed. */
  | 'forbidden'
  /* the gifting agent is behind the per-category AI toggle (card Hsk5cyJ8ap1R) */
  | 'agent_off'
  /* kudos on a request row (card HBYCnnD9q5FS): who may praise what */
  | 'self_kudos'
  | 'already_given'
  /* Settings names (card yFLCjm7ONIEc): the specific reason, not a generic
     failure — the Settings status line prints it verbatim. */
  | 'name_required'
  | 'name_too_long'
  | 'display_name_required'
  | 'display_name_too_long'
  /* Reply-text colour (card 9QADJv3CdiOh): the key was outside the offered
     16 — the Settings status line prints the reason verbatim. */
  | 'bad_reply_colour'
  /* Shared handle (card w8Mj4ThRii6Z) — same rule as the names: the Settings
     status line prints the reason verbatim, so the code names the reason. */
  | 'handle_required'
  | 'handle_bad'
  /* Inbox content routing (card 99VNKrT2nwvy) — the move affordance names
     the reason verbatim, same contract as the Settings names. */
  | 'already_filed'
  | 'quarantined'
  | 'no_link_to_file'
  /* Chore intake (card UZDxtHiJhtRt) — the gate names its own failures. */
  | 'no_pending_proposal'
  | 'already_decided'
  /* Re-route (card NWmSqIsojQyU) — a private message never moves, and a
     target that is not a member of this couple is not a destination. */
  | 'private_item'
  | 'unknown_member'
  | 'handle_taken'
  /* Over-length entry at any door (card YYrJc5OkW7EP): the specific reason,
     so the composer can say "too long" instead of "something went wrong". */
  | 'too_long'
  /* Guess Me round engine (card eq92fFXqQUuP) — the state machine names every
     refusal, so the screen can say which of them it was: the day's three
     rounds are used, there is no round in the state you acted on, it is not
     your role, your submission is already locked, the guess landed before
     the choices existed, or the couple is not paired. */
  | 'cap_reached'
  | 'not_open'
  | 'not_your_turn'
  | 'already_locked'
  | 'too_late'
  | 'not_scoreable'
  | 'not_paired'
  /* Guessmate, the 5-letter duel (card 8gbGPJVD3ch4): every refusal names
     itself so the board can say which one it was — the word was already set,
     the caller is not in this match, the eight guesses are spent, or the word
     did not validate (with its reason). `word_list_unavailable` and
     `word_check_failed` are the two honest "not your fault" answers: no list
     has been chosen yet, or the dictionary could not be reached. */
  | 'already_set'
  | 'not_a_player'
  | 'no_guesses_left'
  | 'word_not_accepted'
  | 'word_list_unavailable'
  | 'word_check_failed'
  /* Meeting transcription (card a0ABwZvTf9ct): the specific failure, so the
     capture sheet can say what happened instead of "something went wrong". */
  | 'stt_unavailable'
  | 'audio_invalid'
  /* The account-to-couple transition is a compare-and-set (card AetvN3T4ZBmK):
     a concurrent link/join moved this account first, so the caller re-reads
     state and retries rather than trusting a silent overwrite. */
  | 'link_conflict'
  /* No-double-billing guard (card ImIQwJDLW6Xs): the account already belongs
     to a couple holding a live subscription, and its current couple could not
     be verified. Both name themselves so the UI can say why the join stopped. */
  | 'couple_subscription_active'
  /* Promo redemption (card yWsCXHAXQ3Ej): each refusal names itself so the
     upgrade sheet can say what happened — bad code, wrong account, or this
     couple already holds a grant. */
  | 'promo_invalid'
  | 'promo_wrong_account'
  | 'promo_already_redeemed'
  | 'couple_check_failed'
  | 'audio_too_large'
  | 'transcription_failed'
  /* Plan, the voice-first weekly meeting (card 7aTCRQ9j8PWV): the specific
     reason, so the Plan screen can say it instead of "something went wrong". */
  | 'transcript_required'
  | 'transcript_too_long'
  | 'session_not_editable'
  | 'already_confirmed'
  | 'session_discarded'
  /* Amending a reviewed meeting after it filed (card DxFGCFSI4t0R): the lines
     can be reworded, but a line that already became a task cannot change into
     a Notebook entry — the row exists, so the shape is the filing's. */
  | 'draft_shape_locked'
  /* Calendar drafts from a meeting (card hCF9D2WCpv7Q): an unknown proposal
     id is a missing resource; `google_not_connected` and
     `calendar_push_failed` are the two answers to "Add to calendar" that
     leave the proposal untouched so the couple can tap again. */
  | 'unknown_calendar_draft'
  | 'google_not_connected'
  | 'calendar_push_failed'
  /* Chores assignment modes (card UeOwZSHfdZCh): the partner claimed it first,
     so the caller needs the conflict named rather than a generic failure. */
  | 'already_claimed'
  /* Gift Concierge V1 T+5 reaction (card Yw5UFURMXSTY): the pick is not
     accepted yet, or has already been answered — both are client-visible
     states the reaction card must render rather than a generic failure. */
  | 'not_accepted'
  | 'already_answered'
  | 'synthesis_failed'
  | 'synthesis_unavailable'
  /* Pear Plus paywall (card 32BEnNO6I2kx): the tap landed on a Plus-only
     surface from an Essentials couple — 402, and the client opens the
     upgrade sheet instead of saying "something went wrong". The three
     Stripe-side codes name the server's own failures, never the user's. */
  | 'plus_required'
  | 'stripe_not_configured'
  | 'stripe_error'
  | 'bad_signature'
  /* Support revoke route (sol-review IsfArxwGo4FX): the ops token is unset,
     so the server simply has no support route — fail-closed, not a user error. */
  | 'support_not_configured'
  /* Web Push (card HWrVePJ5qW4d): the VAPID keys or the D1 binding are
     absent, so push cannot be offered — fail-closed, not a user error. */
  | 'push_not_configured'
  /* Referrals (card 3MWZ8Zs7jA7e): ported from JitsLab. Each refusal names
     itself so the share sheet can say what happened — the link is unknown, it
     is the couple's OWN link (no self-referral), or this couple has already
     used one. `referrals_unconfigured` is the server missing its D1 binding,
     never the user's mistake. */
  | 'referral_unknown'
  | 'referral_self'
  | 'referral_already'
  | 'referrals_unconfigured'
  | 'internal';

/**
 * Nocturne (the two-device game in the Us tab) — one transient frame kind.
 *
 * The room is a PIPE for these: `frame` is opaque game-layer JSON that the
 * Durable Object relays to the partner socket and never interprets, stores,
 * sequences, or writes to the thread history. The only shape the contract
 * enforces is "an object, bounded in size" — every game rule lives in the
 * clients (web/src/games/nocturne/engine.ts).
 */
export type NocturneFrame = { kind: string } & Record<string, unknown>;

/** Serialised-size cap on one Nocturne game frame (8 KB). Lives here beside the
 *  other wire limits so the parser and any client guard read the same number. */
export const MAX_NOCTURNE_FRAME = 8192;

export type ClientMessage =
  | { v: 1; t: 'hello'; token: string }
  | { v: 1; t: 'send'; clientMsgId: string; text: string; wantsAgent?: boolean }
  | { v: 1; t: 'msg-remove'; seq: number }
  | { v: 1; t: 'history'; before?: number; limit?: number; sinceDays?: number }
  /* Nocturne game frame — relay-only. `to` is accepted for forward-compat and
   * must be 'partner': the room has exactly one other seat, so anything else
   * is a malformed frame. */
  | { v: 1; t: 'nocturne'; frame: NocturneFrame; to?: 'partner' }
  /* Card J9hHjSZDFTCV — this device's own visibility. `hidden` is a claim about
   * ONE socket, never about anyone else: it only ever moves the SENDER's entry
   * in the room's in-memory presence map, so a client cannot talk its partner
   * into appearing offline. */
  | { v: 1; t: 'presence'; hidden: boolean }
  /* Card tPPB3ozS4YrK — "I am typing", nothing more. No payload: the room
   * already knows who sent it, and a frame that carried text would turn an
   * ephemeral hint into a second copy of the draft. Carries no sequence, no
   * recipient and no timing — the room stamps `at` when it relays. */
  | { v: 1; t: 'typing' }
  | { v: 1; t: 'ping' };

export type ServerMessage =
  | {
      v: 1;
      t: 'welcome';
      coupleId: string;
      memberId: string;
      role: Role;
      head: number;
      /* Card 2NuQ8dd2JMqs — true when the couple completed while THIS device had
       * no socket, so the `paired` broadcast reached only the attached one and
       * the welcome is the second chance to say so. */
      paired?: boolean;
    }
  | { v: 1; t: 'msg'; message: Message }
  /* An existing message changed in place — the game round's card is rewritten
     when the round reveals (card Bkt2clm8SSOI). Same seq, new text. */
  | { v: 1; t: 'msg-update'; message: Message }
  /* An existing message is gone: a round abandoned by "next topic" takes its
     invitation card out of the thread rather than leaving a dead link. */
  | { v: 1; t: 'msg-remove'; seq: number }
  | { v: 1; t: 'ack'; clientMsgId: string; seq: number }
  | { v: 1; t: 'history'; messages: Message[]; hasMore: boolean }
  | { v: 1; t: 'error'; code: ErrorCode; message: string; ref?: string }
  /* A request row changed (kudos landed): the panel refetches on this. */
  | { v: 1; t: 'requests'; requestId: string; kudosBy: string[] }
  /* A chore row changed — a reaction landed or a done-note was written (card
   * MnorjNDwB3of). The partner's Chores panel refetches so the receipt and
   * the glyph appear without a reload, same hand-off as requests. `choreId` is
   * absent on the plain "something in chores moved" broadcast the settings
   * routes send, so the panel treats it as "refetch all". */
  | { v: 1; t: 'chores'; choreId?: string }
  /* A decision row changed — created, or a vote landed, or the two votes
     settled it (card HMzNgJETH7HE). Same hand-off as chores: the frame carries
     the id, never the sealed picks, and each client re-reads its OWN
     projection of /api/decisions. A partner therefore learns that something
     moved without the broadcast carrying what anyone chose. */
  | { v: 1; t: 'decisions'; decisionId?: string }
  /* The game round moved (answer landed / reveal / closed): refetch. `closed`
   * is what "next topic" leaves behind, so the client cannot assume the round
   * is still playable. */
  | { v: 1; t: 'game'; roundId: string; state: 'open' | 'revealed' | 'closed' }
  /* Guess Me (card eq92fFXqQUuP) — same hand-off as game: the panel refetches
   * its own round. `state` is the round's stored state machine value, so it is
   * deliberately a string rather than a closed union here. */
  | { v: 1; t: 'guessme'; roundId: string; state: string }
  /* Guessmate (card 8gbGPJVD3ch4) — the duel's board moved: a word was set, a
     guess landed or the turn timed out. Same hand-off as guessme; the panel
     refetches the caller's own projection and never receives a word here. */
  | { v: 1; t: 'guessmate'; matchId: string; state: string; turn: number }
  /* Where Am I (card gzUOpwYknx7V) — the same hand-off again: one shared round,
     so both panels refetch when it moves. */
  | { v: 1; t: 'whereami'; roundId: string; state: string }
  /* Half a Map (parent QNCCBnAUtCof) — the same hand-off for the co-op maze:
     the frame says something moved, and each seat re-reads its OWN projection,
     because the two projections are different shapes. */
  | { v: 1; t: 'halfamap'; matchId: string; round: number; state: string }
  /* A display name changed (card yFLCjm7ONIEc): both clients re-read
     /api/pair/status so the partner sees the new name without a reload. */
  | { v: 1; t: 'members' }
  /* The ceremonial pairing moment (card 2NuQ8dd2JMqs): broadcast the instant
   * the second member joins. `milestone` is the room's own label for the
   * moment; `at` is the DO's clock. */
  | { v: 1; t: 'paired'; milestone: string; at: number }
  /* Card J9hHjSZDFTCV — who is currently watching this room. Ephemeral: the
     frame is derived from the DO's socket map at the moment it is sent, is
     never persisted, and a client that goes quiet or closes drops out of the
     NEXT frame. `online` holds member ids only — no read state, no counts, no
     ticks, nothing about what anyone has seen. */
  | { v: 1; t: 'presence'; online: string[]; at: number }
  /* Card tPPB3ozS4YrK — the partner is typing in an open thread. Relay-only:
     the room stamps `at` on the way out and never keeps it, so a receiver can
     run its own clock and drop the hint 4s after it ARRIVED rather than
     trusting two devices' clocks to agree. `memberId` is the sender's, which
     is what lets the client refuse its own echo as a second belt on the
     room's partner-only relay. */
  | { v: 1; t: 'typing'; memberId: string; at: number }
  | { v: 1; t: 'pong' }
  /* Nocturne — the relayed game frame (client → partner), plus the two
   * presence notifications the room itself emits on a seat's first socket
   * connecting and last socket closing. Ephemeral by construction: relayed
   * verbatim, never persisted, never sequenced, never echoed to the sender. */
  | { v: 1; t: 'nocturne'; frame: NocturneFrame };

/** Every frame name the server can send. Derived from the union above, so a
 *  new variant is visible to native ports the moment it is added — card
 *  Ym2Pa1h7xxbY keeps `scripts/contract-parity.mjs` honest against this. */
export type ServerFrameName = ServerMessage['t'];

export type MessageKind = 'text' | 'agent-card' | 'kudos' | 'request-card' | 'question-card' | 'date-card' | 'system';

export interface Message {
  seq: number;            // per-couple monotonic, starts at 1, gapless
  id: string;             // uuid v4 assigned by the DO
  kind: MessageKind;
  fromMemberId: string | null; // null for 'agent-card' (third participant, Phase 0 stub)
  text: string;
  ts: number;             // epoch ms assigned by the DO (single clock = DO blockConcurrencyWhile)
  clientMsgId?: string;   // echo for sender-side de-dupe (uuid v4 from client)
  /** Set only on a game-round card: which round this message belongs to. It is
   *  how one round keeps exactly one card (post once, rewrite on reveal,
   *  delete when abandoned). Absent on every other kind of message. */
  roundId?: string;
}

/** Serialize any ServerMessage for the wire (JSON text frame). */
export function encodeServerMessage(m: ServerMessage): string {
  return JSON.stringify(m);
}

/**
 * clientMsgId prefix reserved for the SERVER (card OAcY41nByea9). The agent
 * card commits under `<AGENT_COMMIT_PREFIX><source message id>`, so
 * reconciliation can find "the card this message was promised" by id alone.
 * parseClientMessage refuses the prefix on the way in: it is the server's
 * namespace, not the client's.
 */
export const AGENT_COMMIT_PREFIX = 'agent-card:';

/**
 * clientMsgId prefix for the METERED concierge turn (card njDMEWNC4maU). The
 * reservation id is derived from the source message — `agent-turn:<msgId>` —
 * instead of a random uuid, because it is the only handle an interrupted run
 * still has: BillingSlice keeps a durable take ledger keyed by it, so the
 * retry recomputes the id, finds the take that is already charged, and gets
 * that turn back instead of paying for a second one. Server-owned, refused by
 * parseClientMessage for the same reason as AGENT_COMMIT_PREFIX.
 */
export const AGENT_TURN_PREFIX = 'agent-turn:';


/** Parse + structurally validate a client frame. Throws `Error` with the code as message. */
export function parseClientMessage(raw: string): ClientMessage {
  let obj: unknown;
  try {
    obj = JSON.parse(raw);
  } catch {
    throw new Error('bad_payload');
  }
  const m = obj as Partial<ClientMessage> & { v?: number; t?: string };
  if (m.v !== 1 || typeof m.t !== 'string') throw new Error('bad_payload');
  switch (m.t) {
    case 'hello':
      if (typeof (m as { token?: string }).token !== 'string') throw new Error('bad_payload');
      break;
    case 'send': {
      const s = m as { clientMsgId?: unknown; text?: unknown; wantsAgent?: unknown };
      if (typeof s.clientMsgId !== 'string' || typeof s.text !== 'string') throw new Error('bad_payload');
      // Server-owned namespaces (card OAcY41nByea9, extended by card
      // njDMEWNC4maU): the agent card commits under AGENT_COMMIT_PREFIX +
      // message id, and a METERED turn reserves under AGENT_TURN_PREFIX +
      // message id. Reconciliation reads those ids to decide whether the card
      // landed and whether the turn is still owed. a client id squatting on
      // either prefix could let a TEXT row impersonate a delivered card — and,
      // via the UNIQUE index on client_msg_id, stop the real card landing at
      // all.
      if (s.clientMsgId.startsWith(AGENT_COMMIT_PREFIX) || s.clientMsgId.startsWith(AGENT_TURN_PREFIX)) {
        throw new Error('bad_payload');
      }
      // The cap lives in shared/security.ts with every other entry limit, so
      // the composer's maxLength and this rejection read the same number
      // (card YYrJc5OkW7EP). 'too_long' is its own code, so the client can
      // say the message is too long instead of "bad payload".
      if (s.text.length === 0) throw new Error('bad_payload');
      if (s.text.length > MAX_THREAD_TEXT) throw new Error('too_long');
      // Invoke-path chip (decision 2026-10-02): optional and strictly boolean so
      // a malformed frame is refused rather than silently coerced to "ask".
      if (s.wantsAgent !== undefined && typeof s.wantsAgent !== 'boolean') {
        throw new Error('bad_payload');
      }
      break;
    }
    case 'msg-remove': {
      // Card — delete your own message. The seq must be a positive integer;
      // the DO enforces OWNERSHIP (you can only remove your own rows), so this
      // parser only checks shape, never the row's sender.
      const r = m as { seq?: unknown };
      if (typeof r.seq !== 'number' || !Number.isInteger(r.seq) || r.seq < 1) throw new Error('bad_payload');
      break;
    }
    case 'history': {
      const h = m as { before?: unknown; limit?: unknown; sinceDays?: unknown };
      if (h.before !== undefined && typeof h.before !== 'number') throw new Error('bad_payload');
      if (h.limit !== undefined && typeof h.limit !== 'number') throw new Error('bad_payload');
      if (h.sinceDays !== undefined && typeof h.sinceDays !== 'number') throw new Error('bad_payload');
      break;
    }
    case 'presence': {
      // Card J9hHjSZDFTCV — strictly boolean, so a malformed frame is refused
      // rather than silently coerced: `hidden: "false"` would otherwise read
      // as truthy and put a partner who is actually watching back offline.
      const p = m as { hidden?: unknown };
      if (typeof p.hidden !== 'boolean') throw new Error('bad_payload');
      break;
    }
    case 'ping':
      break;
    case 'nocturne': {
      /* Relay-only frame: the room never reads `frame`, so the parser checks
         shape and size only — an object (never an array or scalar) whose
         serialised form fits the 8 KB cap. Over-cap is `too_long` (the same
         content mistake as an over-length message: the socket stays open so
         the client can shorten and resend); anything malformed is
         `bad_payload` and closes, like every other kind. */
      const n = m as { frame?: unknown; to?: unknown };
      if (n.frame === null || typeof n.frame !== 'object' || Array.isArray(n.frame)) {
        throw new Error('bad_payload');
      }
      if (n.to !== undefined && n.to !== 'partner') throw new Error('bad_payload');
      if (JSON.stringify(n.frame).length > MAX_NOCTURNE_FRAME) throw new Error('too_long');
      break;
    }
    case 'typing':
      // Card tPPB3ozS4YrK — payload-free by design, so there is nothing to
      // validate: an extra field is ignored rather than refused, because a
      // client adding one must not lose its socket over a hint that costs
      // nothing to drop.
      break;
    default:
      throw new Error('bad_payload');
  }
  return obj as ClientMessage;
}

/**
 * Card J9hHjSZDFTCV — is the presence frame switched on for this worker?
 *
 * Fail-closed by construction: unset, empty, or any value outside the accepted
 * set means OFF, so a typo in the deploy can only ever remove the glow, never
 * switch a feature on by accident. The web client mirrors this through
 * /api/auth/config and requires `=== true`, so the flag is honoured on both
 * sides of the wire.
 */
export function presenceEnabled(env: { PEAR_PRESENCE?: string }): boolean {
  const v = (env.PEAR_PRESENCE ?? '').trim().toLowerCase();
  return v === '1' || v === 'on' || v === 'true';
}

/**
 * Card J9hHjSZDFTCV — how long a presence entry survives without a frame.
 *
 * The socket's own close event is the primary "gone" signal and the client's
 * `hidden` frame is the tab-hidden one; this TTL is the safety net for the
 * case neither event reaches the room (a device that falls off the network
 * with the socket half-open). Evaluated when a snapshot is built, never by a
 * timer, so an idle room costs nothing and no alarm is armed for it.
 */
export const PRESENCE_TTL_MS = 75_000;

/**
 * Card tPPB3ozS4YrK — is the typing indicator switched on for this worker?
 *
 * Same fail-closed contract as presenceEnabled: unset, empty or any value
 * outside the accepted set means OFF, so the worst a deploy typo can do is
 * remove the hint. Off removes BOTH halves — the room stops relaying and
 * /api/auth/config reports false, which is the only thing the client renders
 * from — so there is no state where frames fly and nobody is allowed to show
 * them, or the reverse.
 */
export function typingEnabled(env: { PEAR_TYPING?: string }): boolean {
  const v = (env.PEAR_TYPING ?? '').trim().toLowerCase();
  return v === '1' || v === 'on' || v === 'true';
}

/**
 * Card tPPB3ozS4YrK — the client's floor between two typing frames.
 *
 * One frame per two seconds is the whole rate contract: a partner's keystrokes
 * must not become a stream of socket writes, and the room enforces the same
 * window rather than trusting the client to be polite. A frame inside the
 * window is dropped silently — the sender is not wrong, just early, and an
 * error frame would close a socket over a hint.
 */
export const TYPING_MIN_INTERVAL_MS = 2_000;

/**
 * Card tPPB3ozS4YrK — how long a typing hint survives without a new one.
 *
 * Counted from the frame's ARRIVAL on the receiving device, never from the
 * sender's clock and never by a timer on the room: the room relays and forgets,
 * the receiver holds the hint for four seconds and drops it. That is the
 * "auto-expires after 4s of silence" clause, and it costs an idle room
 * nothing.
 */
export const TYPING_TTL_MS = 4_000;

/** History page size: default 50, hard cap 200. */
export const HISTORY_DEFAULT_LIMIT = 50;
export const HISTORY_MAX_LIMIT = 200;

/* Archive search (card 2sjo3uNvaRDr). The thread hydrates a 7-day window, so
   the browser never holds years of history — findability has to live on the
   server. One search is one bounded read of the couple's own rows: a cap so a
   paste cannot drag the whole text column into a frame, and a floor so a
   single character is not a scan. */
export const SEARCH_DEFAULT_LIMIT = 25;
export const SEARCH_MAX_LIMIT = 50;
export const SEARCH_MIN_QUERY = 2;
